import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing Screen Wake Lock API
 * Prevents the screen from sleeping when active
 * 
 * @param {boolean} isActive - Whether wake lock should be active
 * @returns {Object} - Object containing isWakeLockActive state and error message if any
 */
export const useWakeLock = (isActive) => {
  const [isWakeLockActive, setIsWakeLockActive] = useState(false);
  const [error, setError] = useState(null);
  const wakeLockRef = useRef(null);
  const isSupportedRef = useRef(null);
  const isActiveRef = useRef(isActive);

  // Keep isActive ref in sync
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Check if wake lock is supported
  useEffect(() => {
    isSupportedRef.current = 'wakeLock' in navigator;
    if (!isSupportedRef.current) {
      setError('Wake Lock API is not supported in this browser');
      console.warn('Screen Wake Lock API is not supported');
    }
  }, []);

  // Release wake lock
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setIsWakeLockActive(false);
        console.log('Wake lock released');
      } catch (err) {
        console.error('Error releasing wake lock:', err);
      }
    }
  }, []);

  // Request wake lock
  const requestWakeLock = useCallback(async () => {
    // If not supported, silently fail (don't show error to user)
    if (!isSupportedRef.current || !navigator.wakeLock) {
      return;
    }

    try {
      // Release existing wake lock if any
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }

      // Request new wake lock
      const wakeLock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = wakeLock;
      setIsWakeLockActive(true);
      setError(null);
      
      console.log('Wake lock acquired');

      // Handle wake lock release (e.g., when user switches tabs or locks screen)
      wakeLock.addEventListener('release', () => {
        console.log('Wake lock released');
        setIsWakeLockActive(false);
        wakeLockRef.current = null;
      });
    } catch (err) {
      console.error('Wake lock error:', err);
      setError(err.message);
      setIsWakeLockActive(false);
      // Don't show error for permission denied or other expected errors
      if (err.name !== 'NotAllowedError' && err.name !== 'NotSupportedError') {
        // Could emit analytics event here if needed
      }
    }
  }, []);

  // Handle visibility change - re-acquire wake lock when page becomes visible
  useEffect(() => {
    if (!isSupportedRef.current || !navigator.wakeLock) {
      return;
    }

    const handleVisibilityChange = async () => {
      // If wake lock was active and page becomes visible, re-acquire it
      if (document.visibilityState === 'visible' && isActiveRef.current && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestWakeLock]);

  // Main effect: acquire/release wake lock based on isActive prop
  useEffect(() => {
    if (!isSupportedRef.current || !navigator.wakeLock) {
      return;
    }

    if (isActive) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    // Cleanup on unmount or when isActive changes
    return () => {
      releaseWakeLock();
    };
  }, [isActive, requestWakeLock, releaseWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        releaseWakeLock();
      }
    };
  }, [releaseWakeLock]);

  return { isWakeLockActive, error, isSupported: isSupportedRef.current };
};

